import { createFileRoute, useNavigate, useRouter, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Home, Sparkles, LogIn } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { getOAuthRedirectUri, getPasswordResetRedirectUri, isNative, startNativeOAuth } from "@/lib/nativeAuth";
import logoGreenTrack from "@/assets/logo-mark.png";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";
// @ts-expect-error - JS helper module without types
import { sanitizeName, sanitizeEmail } from "@/lib/validate";


export const Route = createFileRoute("/auth")({
  component: AuthPage,
});

type Mode = "signin" | "signup";

function AuthPage() {
  useSeo({
    title: "Sign In or Create an Account | GreenTrack",
    description:
      "Sign in to GreenTrack or create an account to track your physical presence days toward U.S. naturalization.",
    path: "/auth",
  });
  const navigate = useNavigate();
  const router = useRouter();
  const { t } = useLang();
  const [busy, setBusy] = useState<null | "apple" | "email" | "reset">(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");

  async function sendReset() {
    setError(null);
    setNotice(null);
    if (!email) {
      setError("Enter your email above first, then tap Forgot password.");
      return;
    }
    setBusy("reset");
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: getPasswordResetRedirectUri(),
      });
      if (error) throw error;
      setNotice("Check your inbox for a password-reset link.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not send reset email.");
    } finally {
      setBusy(null);
    }
  }

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active && data.session?.user) navigate({ to: "/app", replace: true });
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN") {
        router.invalidate();
        navigate({ to: "/app", replace: true });
      }
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [navigate, router]);

  async function signInOAuth(provider: "apple") {
    setError(null);
    setNotice(null);
    setBusy(provider);
    try {
      if (isNative()) {
        const result = await startNativeOAuth(provider);
        if (result.error) {
          setError(result.error.message ?? "Sign-in failed");
        }
        setBusy(null);
        return;
      }
      const result = await lovable.auth.signInWithOAuth(provider, {
        redirect_uri: getOAuthRedirectUri("/"),
      });
      if ("error" in result && result.error) {
        setError(result.error.message ?? "Sign-in failed");
        setBusy(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Sign-in failed");
      setBusy(null);
    }
  }

  async function submitEmail(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (!email || !password) {
      setError("Email and password are required.");
      return;
    }
    if (mode === "signup" && password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setBusy("email");
    try {
      if (mode === "signup") {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: getOAuthRedirectUri("/app"),
            data: { full_name: name || undefined },
          },
        });
        if (error) throw error;
        if (!data.session) {
          setNotice("Check your inbox to confirm your email, then sign in.");
          setMode("signin");
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Sign-in failed");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      className="relative mx-auto flex h-[100dvh] w-full max-w-[430px] flex-col overflow-hidden bg-paper text-ink"
      style={{ paddingTop: "max(env(safe-area-inset-top), 20px)" }}
    >
      <div
        aria-hidden
        className="brand-glow pointer-events-none absolute -top-24 left-1/2 h-[420px] w-[620px] -translate-x-1/2"
      />

      <header className="relative flex items-center gap-3 px-5 pb-2 pt-3">
        <img src={logoGreenTrack} alt="GreenTrack" className="h-9 w-auto object-contain" />
        <span className="font-display text-subheading text-ink-deep">GreenTrack</span>
      </header>

      <main className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-5 pb-40" style={{ WebkitOverflowScrolling: "touch" }}>
        <div className="mx-auto w-full max-w-sm pt-4">
          <div className="px-6 py-6 sm:px-8">
            <div className="flex flex-col items-center text-center">
              <img src={logoGreenTrack} alt="GreenTrack" className="h-16 w-auto object-contain" />
              <h1 className="mt-5 font-display text-xl font-bold text-ink-deep">
                {mode === "signin" ? "Sign in to GreenTrack" : "Create your GreenTrack account"}
              </h1>
              <p className="mt-1.5 text-caption text-ink-muted">
                {mode === "signin"
                  ? "Welcome back, pick up where you left off."
                  : "Start tracking your days in the US."}
              </p>
            </div>

            <div className="mt-7 space-y-5">
              <button
                onClick={() => signInOAuth("apple")}
                disabled={busy !== null}
                className="glass glass-lift flex w-full items-center justify-center gap-3 rounded-buttons px-6 py-4 text-sm font-semibold text-ink-deep transition-all active:scale-[0.98] disabled:opacity-50"
              >
                <AppleMark />
                <span>{busy === "apple" ? "Signing in…" : "Sign in with Apple"}</span>
              </button>

              <div className="flex items-center gap-3">
                <span className="h-px flex-1 bg-rule" />
                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-faint">
                  or continue with email
                </span>
                <span className="h-px flex-1 bg-rule" />
              </div>

              <form onSubmit={submitEmail} className="space-y-4">
                {mode === "signup" && (
                  <label className="block">
                    <span className="ml-4 text-caption font-medium text-ink-muted">Name</span>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(sanitizeName(e.target.value))}
                      autoComplete="name"
                      className={inputClass}
                      placeholder="Jane Doe"
                    />
                  </label>
                )}
                <label className="block">
                  <span className="ml-4 text-caption font-medium text-ink-muted">Email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(sanitizeEmail(e.target.value))}
                    autoComplete="email"
                    required
                    className={inputClass}
                    placeholder="you@example.com"
                  />
                </label>
                <label className="block">
                  <span className="ml-4 flex items-center justify-between text-caption font-medium text-ink-muted">
                    Password
                    {mode === "signin" && (
                      <button
                        type="button"
                        onClick={sendReset}
                        disabled={busy !== null}
                        className="mr-1 text-caption text-ink-faint transition-colors hover:text-ink-deep disabled:opacity-50"
                      >
                        {busy === "reset" ? "Sending…" : "Forgot password?"}
                      </button>
                    )}
                  </span>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete={mode === "signin" ? "current-password" : "new-password"}
                    required
                    minLength={mode === "signup" ? 8 : undefined}
                    className={inputClass}
                    placeholder={mode === "signup" ? "At least 8 characters" : "••••••••"}
                  />
                </label>

                <button
                  type="submit"
                  disabled={busy !== null}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-buttons bg-ink-deep px-6 py-4 text-sm font-bold uppercase tracking-[0.18em] text-paper shadow-lg transition-all active:scale-[0.98] disabled:opacity-50"
                >
                  {busy === "email"
                    ? "Working…"
                    : mode === "signin"
                      ? "Sign in"
                      : "Create account"}
                </button>
              </form>

              <button
                type="button"
                onClick={() => {
                  setMode(mode === "signin" ? "signup" : "signin");
                  setError(null);
                  setNotice(null);
                }}
                className="w-full text-caption text-ink-muted transition-colors hover:text-ink-deep"
              >
                {mode === "signin"
                  ? "New here? Create an account"
                  : "Already have an account? Sign in"}
              </button>

              {notice && (
                <div className="rounded-cards border border-brand/50 bg-brand/10 p-4 text-caption text-ink-deep">
                  {notice}
                </div>
              )}
              {error && (
                <div className="rounded-cards border border-destructive/30 bg-destructive/10 p-4 text-caption text-destructive">
                  {error}
                </div>
              )}
            </div>
          </div>

          <p className="mt-6 px-4 text-center text-[11px] leading-relaxed text-ink-faint">
            By continuing, you agree to our{" "}
            <Link to="/privacy" className="underline underline-offset-2">
              Privacy Policy
            </Link>
            . We never resell your data, it&apos;s used only to run the app for you.
          </p>
        </div>
      </main>

      {/* Floating pill nav + detached action */}
      <nav
        className="fixed inset-x-0 bottom-0 z-50 mx-auto flex max-w-[430px] items-center justify-center gap-3 px-4"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 14px)" }}
      >
        <div className="glass-nav flex flex-1 items-center gap-1 rounded-nav p-1.5">
          <PillTab
            active={false}
            icon={<Home size={18} />}
            label={t.nav.home}
            onClick={() => navigate({ to: "/" })}
          />
          <PillTab
            active={false}
            icon={<Sparkles size={18} />}
            label={t.nav.why}
            onClick={() => navigate({ to: "/" })}
          />
          <PillTab
            active
            icon={<LogIn size={18} />}
            label={t.nav.login}
            onClick={() => {}}
          />
        </div>

        <button
          onClick={() => {}}
          aria-label={t.nav.login}
          className="glass-nav flex h-[58px] w-[58px] shrink-0 items-center justify-center rounded-nav text-ink-deep transition-opacity active:opacity-70"
        >
          <LogIn size={22} />
        </button>
      </nav>
    </div>
  );
}

function PillTab({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`flex flex-1 flex-col items-center justify-center gap-1 rounded-nav px-2 py-2.5 transition-colors ${
        active ? "bg-primary text-primary-foreground" : "text-ink-muted"
      }`}
    >
      {icon}
      <span className="text-[11px] font-medium leading-none">{label}</span>
    </button>
  );
}

const inputClass =
  "mt-1.5 block w-full rounded-buttons border border-[var(--glass-edge)] bg-glass px-5 py-4 text-sm text-ink-deep backdrop-blur-md outline-none transition-all placeholder:text-ink-faint focus:border-brand/50 focus:ring-2 focus:ring-brand/20";

function AppleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M16.365 1.43c0 1.14-.46 2.27-1.23 3.08-.83.87-2.18 1.55-3.27 1.47-.13-1.1.42-2.27 1.18-3.05.85-.88 2.32-1.53 3.32-1.5zM20.5 17.34c-.55 1.27-.81 1.84-1.52 2.97-.98 1.58-2.36 3.55-4.07 3.57-1.52.01-1.91-.99-3.97-.98-2.06.01-2.49 1-4.01.99-1.71-.02-3.02-1.81-4-3.39C.34 16.6-.21 11.79 1.36 9.18c1.12-1.86 2.89-2.94 4.56-2.94 1.7 0 2.77.93 4.18.93 1.36 0 2.19-.93 4.16-.93 1.49 0 3.07.81 4.19 2.21-3.68 2.02-3.08 7.28.05 8.89z" />
    </svg>
  );
}
